import type {Box2Like, ColorLike, Vector2Like} from "@cm/math"
import {isColorLike, Vector2} from "@cm/math"
import type {HalGeometry} from "@cm/hal"
import type {WebGl2Context} from "#webgl2/webgl2-context"
import {WebGl2GeometryIndexBuffer} from "#webgl2/webgl2-geometry-index-buffer"
import {WebGl2GeometryVertexAttribute} from "#webgl2/webgl2-geometry-vertex-attribute"
import {
    LOC_COLOR,
    LOC_FLAGS,
    LOC_POSITION,
    LOC_POSITION_NEXT,
    LOC_POSITION_PREV,
    LOC_UV,
    LOC_UV_GRAD,
    VERTEX_FLAG_IS_BOUNDARY,
} from "#webgl2/webgl2-shader-constants"
import {loggerFactory} from "@cm/browser-log"

export class WebGl2Geometry implements HalGeometry {
    constructor(readonly context: WebGl2Context) {
        this.vertexPosition = new WebGl2GeometryVertexAttribute(context.gl, 2)
        this.vertexUv = new WebGl2GeometryVertexAttribute(context.gl, 2)
        this.vertexColor = new WebGl2GeometryVertexAttribute(context.gl, 4)
        this.conservativeRasterizationVertexFlags = new WebGl2GeometryVertexAttribute(context.gl, 1)
        this.conservativeRasterizationVertexPositionPrev = new WebGl2GeometryVertexAttribute(context.gl, 2)
        this.conservativeRasterizationVertexPositionNext = new WebGl2GeometryVertexAttribute(context.gl, 2)
        this.conservativeRasterizationVertexUvGrad = new WebGl2GeometryVertexAttribute(context.gl, 4)
        this.indexBuffer = new WebGl2GeometryIndexBuffer(context.gl)
    }

    // HalEntity
    dispose(): void {
        this.vertexPosition.dispose()
        this.vertexUv.dispose()
        this.vertexColor.dispose()
        this.conservativeRasterizationVertexFlags.dispose()
        this.conservativeRasterizationVertexPositionPrev.dispose()
        this.conservativeRasterizationVertexPositionNext.dispose()
        this.conservativeRasterizationVertexUvGrad.dispose()
        this.indexBuffer.dispose()
    }

    get numVertices() {
        return this._conservativeRasterizationBaseVertex ?? this.vertexPosition.size
    }

    get numIndices() {
        return this._conservativeRasterizationBaseIndex ?? this.indexBuffer.size
    }

    // HalGeometry
    clear(): void {
        this.vertexPosition.clear()
        this.vertexUv.clear()
        this.vertexColor.clear()
        this.conservativeRasterizationVertexFlags.clear()
        this.conservativeRasterizationVertexPositionPrev.clear()
        this.conservativeRasterizationVertexPositionNext.clear()
        this.conservativeRasterizationVertexUvGrad.clear()
        this.indexBuffer.clear()
        this._conservativeRasterizationBaseVertex = undefined
        this._conservativeRasterizationBaseIndex = undefined
    }

    // HalGeometry
    addVertices(positions: Vector2Like[] | Float32Array, uvs?: Vector2Like[] | Float32Array, colors?: ColorLike | ColorLike[] | Float32Array): number {
        this.removeConservativeRasterizationGeometry()
        const numVertices = positions.length / (positions instanceof Float32Array ? 2 : 1)
        const numUVs = uvs ? uvs.length / (uvs instanceof Float32Array ? 2 : 1) : numVertices
        const numColors = colors && !isColorLike(colors) ? colors.length / (colors instanceof Float32Array ? 4 : 1) : numVertices
        if (numVertices !== numUVs) {
            throw Error("Number of positions and uvs must match or uvs must be undefined")
        }
        if (numVertices !== numColors) {
            throw Error("Number of positions and colors must match or colors must be undefined")
        }
        const baseIndex = this.vertexPosition.size
        this.vertexPosition.add(positions)
        this.vertexUv.add(uvs ?? new Float32Array(numVertices * 2))
        this.vertexColor.add(isColorLike(colors) ? Array(numVertices).fill(colors) : (colors ?? new Float32Array(numVertices * 4).fill(1)))
        if (this.vertexPosition.size !== this.vertexUv.size || this.vertexPosition.size !== this.vertexColor.size) {
            throw Error("Internal error: Vertex attributes size mismatch")
        }
        if (this.conservativeRasterization) {
            this.conservativeRasterizationVertexFlags.add(new Float32Array(numVertices))
            this.conservativeRasterizationVertexPositionPrev.add(new Float32Array(numVertices * 2))
            this.conservativeRasterizationVertexPositionNext.add(new Float32Array(numVertices * 2))
            this.conservativeRasterizationVertexUvGrad.add(new Float32Array(numVertices * 4))
            if (
                this.vertexPosition.size !== this.conservativeRasterizationVertexFlags.size ||
                this.vertexPosition.size !== this.conservativeRasterizationVertexPositionPrev.size ||
                this.vertexPosition.size !== this.conservativeRasterizationVertexPositionNext.size ||
                this.vertexPosition.size !== this.conservativeRasterizationVertexUvGrad.size
            ) {
                throw Error("Internal error: Vertex attributes size mismatch with conservative rasterization")
            }
        }
        return baseIndex
    }

    // HalGeometry
    addIndices(indices: number[] | Uint32Array): void {
        this.removeConservativeRasterizationGeometry()
        this.indexBuffer.add(indices)
    }

    // HalGeometry
    removeVertices(startIndex: number, count: number): void {
        this.removeConservativeRasterizationGeometry()
        this.vertexPosition.remove(startIndex, count)
        this.vertexUv.remove(startIndex, count)
        this.vertexColor.remove(startIndex, count)
        if (this.conservativeRasterization) {
            this.conservativeRasterizationVertexFlags.remove(startIndex, count)
            this.conservativeRasterizationVertexPositionPrev.remove(startIndex, count)
            this.conservativeRasterizationVertexPositionNext.remove(startIndex, count)
            this.conservativeRasterizationVertexUvGrad.remove(startIndex, count)
        }
    }

    // HalGeometry
    removeIndices(startIndex: number, count: number): void {
        this.removeConservativeRasterizationGeometry()
        this.indexBuffer.remove(startIndex, count)
    }

    // HalGeometry
    addRect(rectPos: Box2Like, rectUV?: Box2Like, color?: ColorLike): void {
        const baseVertexIndex = this.addVertices(
            [
                new Vector2(rectPos.x, rectPos.y),
                new Vector2(rectPos.x, rectPos.y + rectPos.height),
                new Vector2(rectPos.x + rectPos.width, rectPos.y),
                new Vector2(rectPos.x + rectPos.width, rectPos.y + rectPos.height),
            ],
            rectUV
                ? [
                      new Vector2(rectUV.x, rectUV.y),
                      new Vector2(rectUV.x, rectUV.y + rectUV.height),
                      new Vector2(rectUV.x + rectUV.width, rectUV.y),
                      new Vector2(rectUV.x + rectUV.width, rectUV.y + rectUV.height),
                  ]
                : undefined,
            color,
        )
        this.addIndices([baseVertexIndex, baseVertexIndex + 1, baseVertexIndex + 2])
        this.addIndices([baseVertexIndex + 2, baseVertexIndex + 1, baseVertexIndex + 3])
    }

    // HalGeometry
    addLine(from: Vector2Like, to: Vector2Like, thickness: number, color?: ColorLike): void {
        this.addPath([from, to], thickness, color)
    }

    // HalGeometry
    // TODO make sure geometry is CCW
    addPath(path: Vector2Like[], thickness: number, color?: ColorLike): void {
        if (thickness <= 0) {
            throw Error("Thickness must be positive")
        }
        if (path.length < 2) {
            throw Error("Path must have at least 2 points")
        }
        let baseVertexIndex: number | undefined = undefined
        for (let i = 0; i < path.length; i++) {
            const pos = Vector2.fromVector2Like(path[i])
            const tangent = this.getLengthCorrectedTangent(path, i, closed)
            const perp = tangent.perp()
            const pa = pos.add(perp.mul(thickness / 2))
            const pb = pos.sub(perp.mul(thickness / 2))
            const baseIndex = this.addVertices([pa, pb], undefined, color)
            if (baseVertexIndex === undefined) {
                baseVertexIndex = baseIndex
            }
        }
        if (!baseVertexIndex) {
            throw Error("Invalid path")
        }
        for (let i = 1; i < path.length; i++) {
            this.addIndices([baseVertexIndex + 2 * i - 2, baseVertexIndex + 2 * i - 1, baseVertexIndex + 2 * i])
            this.addIndices([baseVertexIndex + 2 * i - 1, baseVertexIndex + 2 * i + 1, baseVertexIndex + 2 * i])
        }
        if (closed) {
            this.addIndices([baseVertexIndex + 2 * path.length - 2, baseVertexIndex + 2 * path.length - 1, baseVertexIndex])
            this.addIndices([baseVertexIndex + 2 * path.length - 1, baseVertexIndex + 1, baseVertexIndex])
        }
    }

    private addConservativeRasterizationGeometry() {
        if (this._conservativeRasterizationBaseVertex !== undefined && this._conservativeRasterizationBaseIndex !== undefined) {
            // already added conservative rasterization geometry
            return
        }

        // compute the ordered boundary indices
        const orderedBoundaryVertexInfos = this.computeOrderedBoundaryVertexInfos()

        // repeat the boundary vertices 2 times (so we have a total of 3 vertices per boundary vertex)
        const appendVertexAttributeCopy = (attribute: WebGl2GeometryVertexAttribute, index: number, count: number) => {
            for (let i = 0; i < count; i++) {
                attribute.add(attribute.data.subarray(index * attribute.dimensionality, (index + 1) * attribute.dimensionality))
            }
        }
        // const float32ArrayVertexFlagBoundary0 = new Float32Array([VERTEX_FLAG_IS_BOUNDARY | 0])
        const appendConservativeRasterizationVertexData = (
            index: number,
            prevIndex: number,
            nextIndex: number,
            uvGradX: Vector2Like,
            uvGradY: Vector2Like,
            count: number,
        ) => {
            const prevPosition = this.vertexPosition.data.subarray(prevIndex * 2, (prevIndex + 1) * 2)
            const nextPosition = this.vertexPosition.data.subarray(nextIndex * 2, (nextIndex + 1) * 2)
            const uvGrad = new Float32Array([uvGradX.x, uvGradX.y, uvGradY.x, uvGradY.y])
            // adjust the conservative rasterization data for original vertices
            // this.conservativeRasterizationVertexFlags.set(index, float32ArrayVertexFlagBoundary0)
            // this.conservativeRasterizationVertexPositionPrev.set(index, prevPosition)
            // this.conservativeRasterizationVertexPositionNext.set(index, nextPosition)
            // this.conservativeRasterizationVertexUvGrad.set(index, uvGrad)
            // add the conservative rasterization data for duplicated vertices
            for (let i = 0; i < count; i++) {
                this.conservativeRasterizationVertexFlags.add([VERTEX_FLAG_IS_BOUNDARY | i])
                this.conservativeRasterizationVertexPositionPrev.add(prevPosition)
                this.conservativeRasterizationVertexPositionNext.add(nextPosition)
                this.conservativeRasterizationVertexUvGrad.add(uvGrad)
            }
        }
        const appendConservativeRasterizationIndices = (
            boundaryIndex: number,
            numBoundaryVertices: number,
            index: number,
            nextIndex: number,
            copiesBaseIndex: number,
        ) => {
            // add indices for the conservative rasterization geometry
            this.indexBuffer.add([
                index,
                copiesBaseIndex + boundaryIndex * 3 + 0,
                copiesBaseIndex + boundaryIndex * 3 + 1,
                //
                index,
                copiesBaseIndex + boundaryIndex * 3 + 1,
                copiesBaseIndex + boundaryIndex * 3 + 2,
                //
                index,
                copiesBaseIndex + boundaryIndex * 3 + 2,
                nextIndex,
                //
                nextIndex,
                copiesBaseIndex + boundaryIndex * 3 + 2,
                copiesBaseIndex + ((boundaryIndex + 1) % numBoundaryVertices) * 3 + 0,
            ])
        }
        this._conservativeRasterizationBaseVertex = this.vertexPosition.size
        this._conservativeRasterizationBaseIndex = this.indexBuffer.size
        const numCopies = 3
        const numBoundaryVertices = orderedBoundaryVertexInfos.length
        for (let i = 0; i < numBoundaryVertices; i++) {
            const boundaryVertexInfo = orderedBoundaryVertexInfos[i]
            const prevBoundaryVertexInfo = orderedBoundaryVertexInfos[(i - 1 + numBoundaryVertices) % numBoundaryVertices]
            const nextBoundaryVertexInfo = orderedBoundaryVertexInfos[(i + 1) % numBoundaryVertices]
            appendVertexAttributeCopy(this.vertexPosition, boundaryVertexInfo.index, numCopies)
            appendVertexAttributeCopy(this.vertexUv, boundaryVertexInfo.index, numCopies)
            appendVertexAttributeCopy(this.vertexColor, boundaryVertexInfo.index, numCopies)
            appendConservativeRasterizationVertexData(
                boundaryVertexInfo.index,
                prevBoundaryVertexInfo.index,
                nextBoundaryVertexInfo.index,
                boundaryVertexInfo.uvGradX,
                boundaryVertexInfo.uvGradY,
                numCopies,
            )
            appendConservativeRasterizationIndices(
                i,
                numBoundaryVertices,
                boundaryVertexInfo.index,
                nextBoundaryVertexInfo.index,
                this._conservativeRasterizationBaseVertex,
            )
        }
    }

    private removeConservativeRasterizationGeometry() {
        if (this._conservativeRasterizationBaseVertex !== undefined) {
            this.vertexPosition.remove(this._conservativeRasterizationBaseVertex)
            this.vertexUv.remove(this._conservativeRasterizationBaseVertex)
            this.vertexColor.remove(this._conservativeRasterizationBaseVertex)
            this.conservativeRasterizationVertexFlags.remove(this._conservativeRasterizationBaseVertex)
            this.conservativeRasterizationVertexPositionPrev.remove(this._conservativeRasterizationBaseVertex)
            this.conservativeRasterizationVertexPositionNext.remove(this._conservativeRasterizationBaseVertex)
            this.conservativeRasterizationVertexUvGrad.remove(this._conservativeRasterizationBaseVertex)
            this._conservativeRasterizationBaseVertex = undefined
        }
        if (this._conservativeRasterizationBaseIndex !== undefined) {
            this.indexBuffer.remove(this._conservativeRasterizationBaseIndex)
            this._conservativeRasterizationBaseIndex = undefined
        }
    }

    private computeOrderedBoundaryVertexInfos(): BoundaryVertexInfo[] {
        const start = performance.now()

        // go through all triangles and isolate edges which are not shared by another triangle
        const indices = this.indexBuffer.data
        const numIndices = indices.length
        const numTriangles = numIndices / 3
        const edge2key = (index0: number, index1: number): number => {
            if (index0 > index1) {
                const tmp = index0
                index0 = index1
                index1 = tmp
            }
            return index1 * numIndices + index0
        }
        const edgeKeys = new Set<number>()
        const boundaryEdgeByEdgeKey = new Map<number, {index0: number; index1: number}>()
        const triangleIndicesByVertexIndex = new Map<number, number[]>()
        for (let i = 0; i < numTriangles; i++) {
            for (let j = 0; j < 3; j++) {
                const index0 = indices[i * 3 + j]
                const index1 = indices[i * 3 + ((j + 1) % 3)]
                const edgeKey = edge2key(index0, index1)
                if (edgeKeys.has(edgeKey)) {
                    boundaryEdgeByEdgeKey.delete(edgeKey)
                } else {
                    edgeKeys.add(edgeKey)
                    boundaryEdgeByEdgeKey.set(edgeKey, {index0, index1})
                }
                // collect triangle indices for each vertex index
                let triangleIndices = triangleIndicesByVertexIndex.get(index0)
                if (!triangleIndices) {
                    triangleIndices = []
                    triangleIndicesByVertexIndex.set(index0, triangleIndices)
                }
                triangleIndices.push(i)
            }
        }

        // from all boundary edges, create a ordered list of vertex indices (CCW)
        const edgeKeysByIndex = new Map<number, number[]>()
        for (const entry of boundaryEdgeByEdgeKey) {
            const edgeKey = entry[0]
            const edge = entry[1]
            let index0EdgeKeys = edgeKeysByIndex.get(edge.index0)
            if (!index0EdgeKeys) {
                index0EdgeKeys = []
                edgeKeysByIndex.set(edge.index0, index0EdgeKeys)
            }
            index0EdgeKeys.push(edgeKey)
            let index1EdgeKeys = edgeKeysByIndex.get(edge.index1)
            if (!index1EdgeKeys) {
                index1EdgeKeys = []
                edgeKeysByIndex.set(edge.index1, index1EdgeKeys)
            }
            index1EdgeKeys.push(edgeKey)
        }
        const entry = boundaryEdgeByEdgeKey.entries().next().value
        if (!entry) {
            return []
        }
        const [firstEdgeKey, firstEdge] = entry
        boundaryEdgeByEdgeKey.delete(firstEdgeKey)
        const firstIndex = firstEdge.index0
        const orderedBoundaryIndices: number[] = [firstIndex]
        let currentEdgeKey = firstEdgeKey
        let currentIndex = firstEdge.index1
        while (currentIndex !== firstIndex) {
            orderedBoundaryIndices.push(currentIndex)
            const nextEdgeKeys = edgeKeysByIndex.get(currentIndex)
            if (!nextEdgeKeys) {
                throw Error("Invalid edge key index mapping")
            }
            const nextEdgeKey = nextEdgeKeys[0] === currentEdgeKey ? nextEdgeKeys[1] : nextEdgeKeys[0]
            const nextIndex = boundaryEdgeByEdgeKey.get(nextEdgeKey)
            if (!nextIndex) {
                throw Error("Invalid edge key mapping")
            }
            if (nextIndex.index0 !== currentIndex) {
                throw Error("Invalid edge key mapping, next index does not match current index")
            }
            boundaryEdgeByEdgeKey.delete(nextEdgeKey)
            currentEdgeKey = nextEdgeKey
            currentIndex = nextIndex.index1
        }

        // compute the uv gradient for each boundary vertex
        const orderedBoundaryVertexInfos: BoundaryVertexInfo[] = []
        for (let i = 0; i < orderedBoundaryIndices.length; i++) {
            const index = orderedBoundaryIndices[i]
            const uvGradX = new Vector2(0, 0)
            const uvGradY = new Vector2(0, 0)
            const triangleIndices = triangleIndicesByVertexIndex.get(index)
            if (!triangleIndices || triangleIndices.length === 0) {
                throw Error("Invalid triangle indices for vertex index")
            }
            for (const triangleIndex of triangleIndices) {
                const i0 = indices[triangleIndex * 3 + 0]
                const i1 = indices[triangleIndex * 3 + 1]
                const i2 = indices[triangleIndex * 3 + 2]
                // compute the uv gradient for this triangle
                const p0 = this.vertexPosition.data.subarray(i0 * 2, (i0 + 1) * 2)
                const p1 = this.vertexPosition.data.subarray(i1 * 2, (i1 + 1) * 2)
                const p2 = this.vertexPosition.data.subarray(i2 * 2, (i2 + 1) * 2)
                const t0 = this.vertexUv.data.subarray(i0 * 2, (i0 + 1) * 2)
                const t1 = this.vertexUv.data.subarray(i1 * 2, (i1 + 1) * 2)
                const t2 = this.vertexUv.data.subarray(i2 * 2, (i2 + 1) * 2)
                // Edge vectors in position space
                const x1 = p1[0] - p0[0]
                const y1 = p1[1] - p0[1]
                const x2 = p2[0] - p0[0]
                const y2 = p2[1] - p0[1]
                // Edge vectors in texture space
                const s1 = t1[0] - t0[0]
                const t1v = t1[1] - t0[1]
                const s2 = t2[0] - t0[0]
                const t2v = t2[1] - t0[1]
                // Compute determinant of position matrix
                const det = x1 * y2 - x2 * y1
                if (Math.abs(det) < 1e-8) {
                    throw new Error("Degenerate triangle — cannot compute UV gradient.")
                }
                const invDet = 1.0 / det
                // Solve for texture gradients using Cramer's rule
                const dUVdx: Vector2Like = {
                    x: (s1 * y2 - s2 * y1) * invDet,
                    y: (t1v * y2 - t2v * y1) * invDet,
                }
                const dUVdy: Vector2Like = {
                    x: (x1 * s2 - x2 * s1) * invDet,
                    y: (x1 * t2v - x2 * t1v) * invDet,
                }
                // Accumulate the gradients
                uvGradX.addInPlace(dUVdx)
                uvGradY.addInPlace(dUVdy)
            }
            // Normalize the gradients
            uvGradX.divInPlace(triangleIndices.length)
            uvGradY.divInPlace(triangleIndices.length)
            orderedBoundaryVertexInfos.push({index, uvGradX, uvGradY})
        }

        logger.debug(`Computed ordered boundary indices in ${performance.now() - start}ms`)

        return orderedBoundaryVertexInfos
    }

    // returns the number of indices to draw
    prepareGeometry() {
        if (this.conservativeRasterization) {
            this.addConservativeRasterizationGeometry()
        }
        this.vertexPosition.bind(LOC_POSITION)
        this.vertexUv.bind(LOC_UV)
        this.vertexColor.bind(LOC_COLOR)
        if (this.conservativeRasterization) {
            this.conservativeRasterizationVertexFlags.bind(LOC_FLAGS)
            this.conservativeRasterizationVertexPositionPrev.bind(LOC_POSITION_PREV)
            this.conservativeRasterizationVertexPositionNext.bind(LOC_POSITION_NEXT)
            this.conservativeRasterizationVertexUvGrad.bind(LOC_UV_GRAD)
        } else {
            const gl = this.context.gl
            gl.disableVertexAttribArray(LOC_FLAGS)
            gl.disableVertexAttribArray(LOC_POSITION_PREV)
            gl.disableVertexAttribArray(LOC_POSITION_NEXT)
            gl.disableVertexAttribArray(LOC_UV_GRAD)
        }
        this.indexBuffer.bind()
        return this.indexBuffer.size
    }

    private getLengthCorrectedTangent(path: Vector2Like[], index: number, closed: boolean): Vector2 {
        let pn: Vector2 | undefined = undefined
        let p0: Vector2
        let pp: Vector2 | undefined = undefined
        const getPoint = (index: number) => Vector2.fromVector2Like(path[index])
        if (index === 0) {
            if (closed) {
                pn = getPoint(path.length - 1)
                p0 = getPoint(0)
                pp = getPoint(1)
            } else {
                p0 = getPoint(0)
                pp = getPoint(1)
            }
        } else if (index === path.length - 1) {
            if (closed) {
                pn = getPoint(index - 1)
                p0 = getPoint(index)
                pp = getPoint(0)
            } else {
                pn = getPoint(index - 1)
                p0 = getPoint(index)
            }
        } else {
            pn = getPoint(index - 1)
            p0 = getPoint(index)
            pp = getPoint(index + 1)
        }
        if (pn === undefined && pp !== undefined) {
            const d = pp.sub(p0)
            if (d.norm() < 1e-8) {
                throw Error("Invalid path")
            }
            return d.normalized()
        } else if (pp === undefined && pn !== undefined) {
            const d = p0.sub(pn)
            if (d.norm() < 1e-8) {
                throw Error("Invalid path")
            }
            return d.normalized()
        } else if (pp !== undefined && pn !== undefined) {
            const dn = p0.sub(pn)
            const dp = pp.sub(p0)
            if (dn.norm() < 1e-8 || dp.norm() < 1e-8) {
                throw Error("Invalid path")
            }
            const tn = dn.normalized()
            const tp = dp.normalized()
            // correct length such that multiplying the perp by thickness will result in the correct line width
            const dot = tn.dot(tp)
            return tn.add(tp).mul(1 / (1 + dot))
        } else {
            throw Error("Invalid path")
        }
    }

    set conservativeRasterization(value: boolean) {
        if (this._conservativeRasterization === value) {
            return // no change
        }
        if (this.numIndices > 0 || this.numVertices > 0) {
            throw Error("Cannot change conservative rasterization mode after geometry has been added")
        }
        this._conservativeRasterization = value
    }

    get conservativeRasterization(): boolean {
        return this._conservativeRasterization
    }

    private _conservativeRasterization = false
    private _conservativeRasterizationBaseVertex?: number // first vertex added for conservative rasterization
    private _conservativeRasterizationBaseIndex?: number // first index added for conservative rasterization
    // private _orderedBoundaryVertexInfos: BoundaryVertexInfo[] = []
    private vertexPosition: WebGl2GeometryVertexAttribute
    private vertexUv: WebGl2GeometryVertexAttribute
    private vertexColor: WebGl2GeometryVertexAttribute
    private conservativeRasterizationVertexFlags: WebGl2GeometryVertexAttribute
    private conservativeRasterizationVertexPositionPrev: WebGl2GeometryVertexAttribute
    private conservativeRasterizationVertexPositionNext: WebGl2GeometryVertexAttribute
    private conservativeRasterizationVertexUvGrad: WebGl2GeometryVertexAttribute
    private indexBuffer: WebGl2GeometryIndexBuffer
}

type BoundaryVertexInfo = {
    index: number
    uvGradX: Vector2Like
    uvGradY: Vector2Like
}

const logger = loggerFactory.getLogger("webgl2")
